import { action, thunk } from 'easy-peasy'
import type { Action, Thunk } from 'easy-peasy'
import type { BaseLogger } from '@redventures/incense/types'
import type { LoggerAdapterConfig } from '@strata-monorepo/shared-types'
import { ConsoleAdapter } from '@redventures/incense/adapters/ConsoleAdapter'
import { initializeNewRelic } from '@strata-monorepo/find-my-plan/src/lib/questionnaire/base/NewRelic/NewRelicSnippet'
import { NewRelicBrowserAdapter } from '@redventures/incense/adapters/NewRelicBrowserAdapter'
import { NoopAdapter } from '@redventures/incense/adapters/NoopAdapter'
import type { FMPStoreModel } from '../../types'
import { getRvUserType } from '@strata-monorepo/utils/rvUserType'

export interface LoggerModel {
  logger: BaseLogger | null
  setLogger: Action<LoggerModel, BaseLogger>
  setLoggerToConsole: Action<LoggerModel>
  checkIfLoggingProviderLoaded: Thunk<LoggerModel>
  bootstrapLogger: Thunk<LoggerModel, void, null, FMPStoreModel>
}

const loggerModel = (): LoggerModel => ({
  logger: null,

  // Actions
  setLogger: action((state, payload) => {
    state.logger = payload
  }),

  setLoggerToConsole: action((state) => {
    state.logger = new ConsoleAdapter()
  }),

  // Thunks
  checkIfLoggingProviderLoaded: thunk(async () => {
    let isNewRelicLoaded: boolean
    try {
      isNewRelicLoaded = await initializeNewRelic()
    } catch (error) {
      console.error('Failed to initialize logger', error)
      isNewRelicLoaded = false
    }

    return isNewRelicLoaded
  }),

  bootstrapLogger: thunk(async (actions, _, { getStoreState, getStoreActions }) => {
    const { setLogger, setLoggerToConsole } = getStoreActions().logger

    const storeState = getStoreState()
    const environment = storeState.env.environment
    const incenseBackendUrl = storeState.env.incenseBackendUrl

    // Non production defaults to console adapter
    if (environment !== 'production') {
      setLoggerToConsole()
      return
    }

    const isLoggerProviderLoaded: boolean = await actions.checkIfLoggingProviderLoaded()

    if (!isLoggerProviderLoaded) {
      setLoggerToConsole()
      return
    }

    try {
      const loggerConfig: LoggerAdapterConfig = {
        userType: getRvUserType(),
        incenseBackendUrl,
        environment,
      }

      const logger = new NewRelicBrowserAdapter(loggerConfig)
      setLogger(logger)
    } catch (error) {
      console.error('Failed to initialize New Relic logger', error)
      const logger = new NoopAdapter()
      setLogger(logger)
    }
  }),
})

export default loggerModel
